/** Classe contant les éléments d'entête du tableau - Doit être utilisé pour la définition des colonnes du tableau */
import {DataTypeEnum} from "@/components/commons/Generics/GenericTable/GenericTableEnums";
import {ICssClass} from "@/components/commons/Generics/GenericTable/GenericTableCssClass";
import EnumItem from "@/types/fields/EnumItem"

export interface IHeader {
    Name: string;
    Label: string;
    DataType: DataTypeEnum;
    ShowLabel: boolean;
    Width: number;
    HideMenu: boolean;
    Tooltip: string;
    ShowColumn: boolean;
    Sort: boolean;
    SortKey: string;
    CssClasses: ICssClass[];
}

/** Définition d'une colonne */
class Header implements IHeader {
    private _name: string;
    private _label: string;
    private _dataType: DataTypeEnum;
    private _showLabel: boolean;
    private _width: number;
    private _tooltip: string;
    private _hideMenu: boolean;
    private _showColumn: boolean;
    private _sort: boolean;
    private _sortKey: string;
    private _cssClasses: ICssClass[];

    public constructor(name: string, label: string, dataType: DataTypeEnum, data: Partial<Header>) {
        this._name = name;
        this._label = label;
        this._dataType = dataType;
        this._showLabel = data?.ShowLabel ?? true;
        this._width = data?.Width ?? undefined;
        this._tooltip = data?.Tooltip ?? undefined;
        this._hideMenu = data?.HideMenu ?? false;
        this._showColumn = data?.ShowColumn ?? true;
        this._sort = data?.Sort ?? true;
        this._sortKey = data?.SortKey ?? undefined;
        this._cssClasses = data?.CssClasses ?? [];
    }

    /** Nom de la colonne */
    get Name() {
        return this._name;
    }

    public set Name(value: string) {
        this._name = value;
    }

    /** Label affiché dans le header de la colonne */
    get Label() {
        return this._label;
    }

    public set Label(value: string) {
        this._label = value;
    }

    /** Type de données dans les cellules de la colonne */
    get DataType() {
        return this._dataType;
    }

    public set DataType(value: DataTypeEnum) {
        this._dataType = value;
    }

    /** Permet d'indiquer l'affichage ou non de l'entête de la colonne */
    get ShowLabel() {
        return this._showLabel;
    }

    public set ShowLabel(value: boolean) {
        this._showLabel = value;
    }

    /** Taille de la colonne */
    get Width() {
        return this._width;
    }

    public set Width(value: number) {
        this._width = value;
    }

    /** Information complémentaire de la colonne */
    get Tooltip() {
        return this._tooltip;
    }

    public set Tooltip(value: string) {
        this._tooltip = value;
    }

    /** Indique si le champ est disponible ou non dans la liste des champs cochables ou dé-cochables */
    get HideMenu() {
        return this._hideMenu;
    }

    public set HideMenu(value: boolean) {
        this._hideMenu = value;
    }

    /** Indique si le champ est caché ou non et donc que la colonne est affichée ou non dans le tableau cible */
    get ShowColumn() {
        return this._showColumn;
    }

    public set ShowColumn(value: boolean) {
        this._showColumn = value;
    }

    /** La colonne doit être triée ? */
    get Sort() {
        return this._sort;
    }

    public set Sort(value: boolean) {
        this._sort = value;
    }

    /** la clé de tri de la colonne Si celle-ci n'est pas la même que le nom de la colonne */
    public get SortKey(): string {
        return this._sortKey;
    }

    public set SortKey(value: string) {
        this._sortKey = value;
    }

    /** Les classes de style pouvant être ajoutées */
    get CssClasses(): ICssClass[] {
        return this._cssClasses;
    }

    public set CssClasses(value: ICssClass[]) {
        this._cssClasses = value;
    }
}

/** Définition d'une colonne de type String */
export class HeaderString extends Header {
    public constructor(name: string, label: string, data: Partial<HeaderString>) {
        super(name, label, DataTypeEnum.String, data);
    }
}

/** Définition d'une colonne de type Decimal */
export class HeaderDecimal extends Header {
    private _minDecimal: number;
    private _maxDecimal: number;

    public constructor(name: string, label: string, data: Partial<HeaderDecimal>) {
        super(name, label, DataTypeEnum.Decimal, data);
        this._minDecimal = data?.MinDecimal ?? 0;
        this._maxDecimal = data?.MaxDecimal ?? 2;
    }

    public get MinDecimal(): number {
        return this._minDecimal;
    }

    public set MinDecimal(value: number) {
        this._minDecimal = value;
    }

    public get MaxDecimal(): number {
        return this._maxDecimal;
    }

    public set MaxDecimal(value: number) {
        this._maxDecimal = value;
    }
}

/** Définition d'une colonne de type Booléen */
export class HeaderBoolean extends Header {
    private _trueValue: any;

    public constructor(name: string, label: string, data: Partial<HeaderBoolean>) {
        super(name, label, DataTypeEnum.Boolean, data);
        this._trueValue = data?.TrueValue ?? true;
    }

    get TrueValue(): any {
        return this._trueValue;
    }

    set TrueValue(value: any) {
        this._trueValue = value;
    }
}

/** Définition d'une colonne de type Date */
export class HeaderDate extends Header {
    public constructor(name: string, label: string, data: Partial<HeaderDate>) {
        super(name, label, DataTypeEnum.Date, data);
    }
}

/** Définition d'une colonne de type personnalisable */
export class HeaderCustom extends Header {
    constructor(name: string, label: string, data: Partial<HeaderString>) {
        super(name, label, DataTypeEnum.Custom, data);
    }
}

export enum IconType {
    Path = 'Path',
    Bootstrap = 'Bootstrap',
    FontAwesome = 'FontAwesome',
}

/** Définition d'une colonne de type image contenant un lien */
export class HeaderIcon extends Header {
    private _iconType: IconType;

    public constructor(name: string, label: string, data: Partial<HeaderIcon>) {
        super(name, label, DataTypeEnum.Icon, data);
        this._iconType = data?.IconType ?? IconType.Bootstrap;
    }

    /** Type d'icône */
    public get IconType(): IconType {
        return this._iconType;
    }

    /** Type d'icône */
    public set IconType(value: IconType) {
        this._iconType = value;
    }
}

/** Définition d'une colonne de type liste de données */
export class HeaderList extends Header {
    private _list: EnumItem[];

    public constructor(name: string, label: string, data: Partial<HeaderList>) {
        super(name, label, DataTypeEnum.List, data);
        this._list = data?.List ?? [];
    }

    /** Liste des différentes valeurs pour l'affichage */
    public get List(): EnumItem[] {
        return this._list;
    }

    /** Liste des différentes valeurs pour l'affichage */
    public set List(value: EnumItem[]) {
        this._list = value;
    }
}
